import React, { Suspense } from 'react';
import styled from 'styled-components';
import { IBaseComponentProps, Typography, FlexBox, Illustration, IPalette, StackPanel } from '@agilepoint/wat-react-ux';
import { IllustrationName } from '@agilepoint/web-components';
import { generateId } from '@agilepoint/utils-common';
import { Localize } from '@agilepoint/wat-react';

interface IIllustrationMessageProps extends IBaseComponentProps<HTMLDivElement> {
    type: IllustrationName;
    displayMessage?: string;
    title?: string;
    color?: 'blue' | 'black';
    width?: string;
    height?: string;
    onRenderMessageContent?(): React.ReactNode;
}

function getColor(palette: IPalette, color?: string) {
    switch (color) {
        case 'black':
            return palette.neutralPrimary;
        case 'blue':
            return palette.themePrimary;
        default:
            return palette.neutralPrimary;
    }
}

export const IllustrationContentBlock = styled(Typography.Small) <Partial<IIllustrationMessageProps>>`
    color: ${({ color, theme: { semantics: { palette } } }) => getColor(palette, color)};
`;

const Title = styled(Typography.Medium)`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
`;

const MessageContentBlock = styled(StackPanel)`
    text-align: center;
    padding: 0 12px;
`;

const StyledFlexBox = styled(FlexBox)`
    margin: 20px auto;
`;

export function IllustrationLoadingMessage() {
    return (
        <FlexBox alignItems="center" justifyContent="center" flexDirection="row" >
            <Typography.Small><Localize translate="common.messages.loading" /></Typography.Small>
        </FlexBox>
    );
}

export const IllustrationMessage = React.memo((props: IIllustrationMessageProps) => {
    const { displayMessage, type, title, color, className, onRenderMessageContent, width = '100%', height = '100%' } = props;
    const renderMessage = React.useCallback(() => {
        if (onRenderMessageContent !== undefined) {
            return <div>{onRenderMessageContent()}</div>;
        }
        return (displayMessage ? <IllustrationContentBlock color={color}>{displayMessage}</IllustrationContentBlock> : <></>);
    }, [onRenderMessageContent, color, displayMessage]);

    return (
        <Suspense fallback={<IllustrationLoadingMessage />}>
            <FlexBox width={width} height={height} className={className} alignItems="center" flexDirection="column" justifyContent="center">
                <StyledFlexBox justifyContent="center" className='ap-illustration-wrapper'>
                    <Illustration type={type} key={generateId()} width="200px" height="200px" />
                </StyledFlexBox>
                <MessageContentBlock orientation="vertical" gap="4" alignment="center">
                    {title ? <Title title={title}>{title}</Title> : <></>}
                    {renderMessage()}
                </MessageContentBlock>
            </FlexBox>
        </Suspense>
    );
});
